import { useState, useEffect } from "react";
import { C } from "../lib/colors";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { Logo } from "../components/Logo";
import { PasswordInput } from "../components/PasswordInput";
import { MinimalFooter } from "../components/MinimalFooter";

// Landing page after a user clicks the password-reset email link.
// Supabase parses the recovery token from the URL hash automatically and
// gives us a temporary session. We prompt for a new password, save it, then
// route them into the correct portal (owner → AgencyOS, client → ClientPortal).

export const ResetPasswordPage = ({ onNavigate, onOwnerLogin, onClientLogin }) => {
  const [status, setStatus] = useState("loading"); // loading | needsPassword | error
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setStatus("error");
      setErrorMessage("Authentication isn't configured on this site.");
      return;
    }
    let cancelled = false;
    const init = async () => {
      // Give Supabase a tick to parse the recovery token from the URL hash.
      await new Promise(r => setTimeout(r, 150));
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user) {
        setStatus("error");
        setErrorMessage(
          "This reset link is no longer valid. It may have expired or already been used. " +
          "Request a fresh one from the login page."
        );
        return;
      }
      setUser(session.user);
      setStatus("needsPassword");
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const handleSetPassword = async () => {
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErrorMessage("Passwords don't match.");
      return;
    }
    setSaving(true); setErrorMessage("");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw new Error(error.message);

      const role = user.app_metadata?.role || user.user_metadata?.role;
      window.history.replaceState({}, "", "/");

      if (role === "owner") {
        onOwnerLogin();
        return;
      }

      const { data: row } = await supabase
        .from("clients")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      const clientProfile = row
        ? {
            id:         row.id,
            email:      row.email,
            name:       row.name || user.user_metadata?.name || "Client",
            company:    row.company || "",
            plan:       row.plan || "Growth",
            joinDate:   row.join_date,
            stripeLink: row.stripe_link,
          }
        : {
            id:    user.id,
            email: user.email,
            name:  user.user_metadata?.name || "Client",
            plan:  "Growth",
          };
      onClientLogin(clientProfile);
    } catch (err) {
      setErrorMessage(err.message || "Could not save password. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#000000",fontFamily:"'Instrument Sans',sans-serif",color:C.text,
      display:"flex",flexDirection:"column"}}>
      <nav style={{padding:"0 5%",height:96,display:"flex",alignItems:"center",justifyContent:"center",
        borderBottom:`1px solid ${C.border}`}}>
        <Logo size={44}/>
      </nav>

      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:440,animation:"fadeUp 0.4s ease"}}>

          {status === "loading" && (
            <div style={{textAlign:"center",color:C.muted}}>
              <div style={{width:40,height:40,margin:"0 auto 16px",border:`3px solid ${C.border}`,
                borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <div style={{fontSize:13}}>Verifying your reset link…</div>
            </div>
          )}

          {status === "error" && (
            <div style={{textAlign:"center"}}>
              <div style={{width:64,height:64,margin:"0 auto 18px",borderRadius:"50%",
                background:"rgba(255,82,82,0.12)",border:`2px solid ${C.red}`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,color:C.red}}>⚠</div>
              <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:800,letterSpacing:-1,marginBottom:10}}>
                Reset link unavailable
              </h1>
              <p style={{color:C.muted,fontSize:14,lineHeight:1.65,marginBottom:24}}>{errorMessage}</p>
              <button onClick={()=>onNavigate("forgot-password")} style={{background:C.green,color:"#000",border:"none",borderRadius:10,
                padding:"12px 26px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
                Request a new link
              </button>
            </div>
          )}

          {status === "needsPassword" && (
            <>
              <div style={{textAlign:"center",marginBottom:32}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                  width:56,height:56,borderRadius:16,background:`linear-gradient(135deg,${C.green},${C.blue})`,
                  fontSize:24,marginBottom:18}}>🔒</div>
                <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:26,fontWeight:800,letterSpacing:-1,marginBottom:8}}>
                  Set a new password
                </h1>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>
                  For <strong style={{color:C.green}}>{user?.email}</strong>
                </p>
              </div>

              <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:18,padding:28}}>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    New password
                  </div>
                  <PasswordInput
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                  />
                </div>

                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    Confirm new password
                  </div>
                  <PasswordInput
                    value={confirm}
                    onChange={e=>setConfirm(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSetPassword()}
                    placeholder="Type it again"
                    autoComplete="new-password"
                  />
                </div>

                {errorMessage && (
                  <div style={{fontSize:12,color:C.red,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                    <span>⚠</span>{errorMessage}
                  </div>
                )}

                <button onClick={handleSetPassword} disabled={saving||!password||!confirm}
                  style={{width:"100%",background:saving||!password||!confirm?"rgba(255,255,255,0.06)":C.green,
                    color:saving||!password||!confirm?C.muted:"#000",border:"none",borderRadius:10,
                    padding:"13px",fontSize:14,fontWeight:700,
                    cursor:saving||!password||!confirm?"not-allowed":"pointer",
                    fontFamily:"'Instrument Sans',sans-serif",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {saving
                    ? <><span style={{width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",
                        borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Saving…</>
                    : "Save & Continue →"}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
      <MinimalFooter onNavigate={onNavigate}/>
    </div>
  );
};
